import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import { Link } from "react-router-dom";
import { withRouter } from '../../../common/withRouter';
import { ButtonOBSOLETED, Notification, Message } from "../../../web.common";
import { LayoutContext } from "../../layout/siteLayout";
import PageTop from "../../layout/PageTop";
import withApi from "../../higherOrderComponents/withApi";
import { DisplayDate } from "../../items";
import { apiErrorText, buttonRowStyle, externalLink, formatMinor, panelStyle, Pager, warningTextStyle } from "./shared";

const pageSize = 50;
const statusOptions = ["new", "processing", "completed", "failed", "cancelled"].map((value) => ({ value, label: value }));

// Payments of the clubhouse wannas. Refunds are a button per settled ("completed") order with a
// confirmation panel; a partial refund is optional (amount in DKK, empty means the full amount).
const Payments = ({ api, searchParams }) => {
  const [wannaCode, setWannaCode] = useState(searchParams?.get("wannaCode") || "");
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [notification, setNotification] = useState(null);
  const [refunding, setRefunding] = useState(null); // the order row being refunded
  const [partialAmount, setPartialAmount] = useState("");
  const [freeSeat, setFreeSeat] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const loadData = async (query, nextSkip) => {
    try {
      const result = await api.clubhouseAdmin.getPayments({
        status: query.status?.value || null,
        wannaCode: query.wannaCode.trim() || null,
        take: pageSize,
        skip: nextSkip,
      });
      setRows(result.orders);
      setTotal(result.total);
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to load the payments.")));
    }
  };

  useEffect(() => {
    loadData({ status: null, wannaCode }, 0);
  }, []);

  const search = (e) => {
    e?.preventDefault();
    setSkip(0);
    setRefunding(null);
    loadData({ status, wannaCode }, 0);
  };

  const changePage = (nextSkip) => {
    setSkip(nextSkip);
    loadData({ status, wannaCode }, nextSkip);
  };

  const openRefund = (order) => {
    setRefunding(order);
    setPartialAmount("");
    setFreeSeat(false);
  };

  const closeRefund = () => {
    setRefunding(null);
    setPartialAmount("");
    setFreeSeat(false);
  };

  // Empty means full refund. Otherwise a DKK amount (e.g. "50" or "50,25") turned into øre.
  const partialMinor = () => {
    const text = partialAmount.trim().replace(",", ".");
    if (text === "") return { minor: null };
    const value = Number(text);
    if (!Number.isFinite(value) || value <= 0) return { error: "The partial amount must be a positive number." };
    const minor = Math.round(value * 100);
    if (minor > refunding.amountMinor) return { error: "The partial amount is more than the order amount." };
    return { minor };
  };

  const refund = async () => {
    const { minor, error } = partialMinor();
    if (error) {
      setNotification(new Message("error", error));
      return;
    }

    setIsBusy(true);
    try {
      const result = await api.clubhouseAdmin.refundPayment(refunding.orderHandle, minor, freeSeat);
      const seat = result.withdrawn ? " The player has left the game: the seat is free." : " The player keeps the seat.";
      if (!result.ok) {
        setNotification(new Message("error", "Frisbii refused the refund. The refund row stays failed: it can be retried from here. The player keeps the seat."));
      } else if (result.note === "already-refunded") {
        setNotification(new Message("warning", `This order was already refunded: nothing was sent.${seat}`));
      } else {
        setNotification(new Message("success", `Refund sent for order ${refunding.orderHandle}${minor ? ` (${formatMinor(minor, refunding.currencyIsoCode)})` : " (full amount)"}.${seat}`));
      }
      closeRefund();
      loadData({ status, wannaCode }, skip);
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to refund the order.")));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className="content-with-right-pane-delimiter">
            <PageTop documentTitle="Clubhouse payments" documentDescription="Clubhouse payments" />
            <h2 className="main-page-header">Clubhouse payments</h2>

            <form onSubmit={search} style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap", margin: "1rem 0" }}>
              <label>
                Order status<br />
                <span style={{ display: "inline-block", minWidth: "12rem" }}>
                  <ReactSelect
                    isClearable
                    placeholder="Any"
                    value={status}
                    onChange={setStatus}
                    options={statusOptions}
                  />
                </span>
              </label>
              <label>
                Wanna code<br />
                <input name="wannaCode" placeholder="w63" value={wannaCode} onChange={({ target }) => setWannaCode(target.value)} />
              </label>
              <ButtonOBSOLETED type="submit" buttonContent="Search" />
            </form>

            {refunding && (
              <div style={panelStyle}>
                <h3>Refund order {refunding.orderHandle}?</h3>
                <p>
                  Player {refunding.userName || refunding.userId}, amount {formatMinor(refunding.amountMinor, refunding.currencyIsoCode)}.
                  A refund is sent to Frisbii and recorded as a new transaction on the charge; the charge is not changed.
                  Only settled (completed) orders can be refunded.
                </p>
                <label>
                  Partial amount in {refunding.currencyIsoCode} (optional: empty refunds the full amount)<br />
                  <input value={partialAmount} placeholder="full amount" onChange={({ target }) => setPartialAmount(target.value)} />
                </label>
                <label style={{ display: "block", marginTop: "0.75rem" }}>
                  <input type="checkbox" checked={freeSeat} onChange={({ target }) => setFreeSeat(target.checked)} />{" "}
                  Also free the seat (the player leaves the game)
                </label>
                <p style={warningTextStyle}>
                  {freeSeat
                    ? "The player leaves the game as if they left from the app. Before the signup deadline a confirmed game that drops below its minimum is open again. Not possible for the game's creator or once the game has started."
                    : "Without this, the refund only moves money: the player keeps the seat and still counts toward the game's players."}
                </p>
                <div style={buttonRowStyle}>
                  <ButtonOBSOLETED buttonContent="Confirm refund" disabled={isBusy} onClick={refund} />
                  <ButtonOBSOLETED buttonContent="Back" disabled={isBusy} onClick={closeRefund} />
                </div>
              </div>
            )}

            {rows == null ? (
              <span>Loading...</span>
            ) : (
              <article className="data-table-wrap">
                <table className="data-lists component-data-table cell-content-align-top">
                  <thead>
                    <tr>
                      <td>Order</td>
                      <td>Wanna</td>
                      <td>Player</td>
                      <td>Status</td>
                      <td>Amount</td>
                      <td>Error</td>
                      <td>Created</td>
                      <td>Frisbii</td>
                      <td>Action</td>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((order) => (
                      <tr key={order.orderHandle}>
                        <td>{order.orderHandle}</td>
                        <td>{order.wannaCode ? <Link to={`/clubhouse-wanna/${encodeURIComponent(order.wannaCode)}`}>{order.wannaCode}</Link> : "-"}</td>
                        <td><Link to={`/user/${order.userId}`}>{order.userName || order.userId}</Link></td>
                        <td>{order.status}</td>
                        <td>{formatMinor(order.amountMinor, order.currencyIsoCode)}</td>
                        <td>{order.errorText || "-"}</td>
                        <td><DisplayDate>{order.createdAtUtc}</DisplayDate></td>
                        <td>{externalLink(order.frisbiiUrl)}</td>
                        <td>
                          <ButtonOBSOLETED
                            buttonContent="Refund"
                            disabled={isBusy || order.status !== "completed" || refunding !== null}
                            onClick={() => openRefund(order)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </article>
            )}
            <Pager skip={skip} take={pageSize} total={rows ? total : null} onChange={changePage} />
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const PaymentsPage = withRouter(
  withApi(
    connect(
      ({ user }) => ({ isLoggedIn: !!user }),
      {}
    )(Payments)
  )
);

PaymentsPage.requiresLogin = true;
PaymentsPage.propTypes = { isLoggedIn: PropTypes.bool };

export default PaymentsPage;
